import { Link } from 'react-router-dom';
import { FEATURED } from '@/lib/banks';
import { formatBps } from '@/lib/yield';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

export default function HysaRatesAugust2026() {
  useDocumentMeta(
    'HYSA rate check: August 2026 · RateMyHYSA',
    'Our August 2026 audit of high-yield savings rates: what changed, who cut, and where the best tax-equivalent yield sits.'
  );

  const total = (a: (typeof FEATURED)[number]) => a.apyBps + (a.bonusBps ?? 0);
  const banks = FEATURED.filter((a) => !a.isReserve).sort((a, b) => total(b) - total(a));
  return (
    <main className="wrap">
      <article className="post">
        <div className="post-date">August 2026 · Monthly audit</div>
        <h1>HYSA rate check: August 2026</h1>
        <p>
          On August 23 we re-verified the published savings rate of every institution we track,
          directly on each bank&rsquo;s website, and archived a dated screenshot of each rates page
          in our audit record. Here&rsquo;s where the market stands.
        </p>

        <h2>The leaderboard</h2>
        <ul>
          {banks.slice(0, 6).map((b) => (
            <li key={b.id}>
              <strong>{b.institution}</strong> ({b.product}): {formatBps(total(b))} APY
              {b.bonusBps ? ` (includes a ${formatBps(b.bonusBps)} promotional bonus)` : ''}
              {b.minToEarn > 0 ? `, $${b.minToEarn.toLocaleString()} minimum to earn` : ''}
              {' · '}
              {b.type === 'BANK_ACCOUNT' ? 'Bank account' : 'Cash management'} ·{' '}
              {b.insurance === 'FDIC' ? 'FDIC insured' : b.insurance === 'SIPC' ? 'SIPC protected' : 'Not insured'}
            </li>
          ))}
        </ul>

        <h2>The tax-equivalent view</h2>
        <p>
          Headline APY is a pre-tax number. Savings interest is fully taxable as ordinary income,
          while FutureMoney Reserve&rsquo;s underlying holdings are Treasury ETFs (state-tax-exempt)
          or municipal bond ETFs (federal-tax-exempt) depending on your income. For a California
          filer earning $100k–$250k, Reserve&rsquo;s 4.50% pre-tax (3.60% base + 0.25% IRA match +
          0.65% welcome boost) works out to about a <strong>5.00% tax-equivalent yield</strong>,
          ahead of every FDIC account on this month&rsquo;s board.{' '}
          <Link to="/#check">Run your own numbers here.</Link>
        </p>

        <h2>What changed this month</h2>
        <p>
          This is our baseline month: every rate above is the freshly verified starting point.
          From September onward, this section will list every cut and raise since the prior audit,
          with before/after screenshots.
        </p>

        <h2>Methodology</h2>
        <p>
          Rates are read from each institution&rsquo;s public rates page on the first business day of
          the month. A full-page screenshot of each source page is archived with a timestamp. We list
          the highest advertised tier available nationally without a promotional relationship
          requirement, and note any minimums or direct-deposit conditions. Tax-equivalent yields use
          2026 single-filer federal and state marginal brackets at an assumed income for each bracket.
        </p>
      </article>
    </main>
  );
}
